import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Download, Upload, CheckCircle2, AlertTriangle, Loader2, Users, Link2 } from 'lucide-react';

/**
 * Radio → Bulk Phone Import panel.
 *
 * Unblocks real-world WhatsApp Radio adoption:
 *   1. Sync-from-users: one click mirrors phones from dashboard `users` docs
 *      down to matching `people` docs (by email / name).
 *   2. Download a pre-filled CSV of the staff STILL missing a phone.
 *   3. Admin fills phones in Excel/Sheets, re-uploads, done.
 *
 * Coverage is computed cross-collection — a person is "reachable" if phone
 * exists on EITHER their people doc or a matching dashboard users doc.
 */
export default function RadioPhoneImportPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingReverse, setSyncingReverse] = useState(false);
  const [result, setResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [reverseSyncResult, setReverseSyncResult] = useState(null);
  const fileRef = useRef(null);

  const loadStats = () => {
    setLoading(true);
    api.get('/radio/phones/stats')
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStats(); }, []);

  const downloadMissing = async () => {
    try {
      const r = await api.get('/radio/phones/missing-csv', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `radio_phones_missing_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded — fill the phone column and re-upload');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/radio/phones/bulk-import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`${r.data.updated} phone${r.data.updated === 1 ? '' : 's'} updated`);
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSyncFromUsers = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/radio/phones/sync-from-users');
      setSyncResult(r.data);
      if (r.data.synced > 0) {
        toast.success(`${r.data.synced} phone${r.data.synced === 1 ? '' : 's'} mirrored from dashboard users`);
      } else {
        toast.info('No new phones to sync — everything is already in place');
      }
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncToUsers = async () => {
    setSyncingReverse(true);
    setReverseSyncResult(null);
    try {
      const r = await api.post('/radio/phones/sync-to-users');
      setReverseSyncResult(r.data);
      if (r.data.synced > 0) {
        toast.success(`${r.data.synced} dashboard user${r.data.synced === 1 ? '' : 's'} updated with HR phone`);
      } else {
        toast.info('Users already in sync with HR master');
      }
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reverse sync failed');
    } finally {
      setSyncingReverse(false);
    }
  };

  const pct = stats?.coverage_pct ?? 0;
  const missing = stats?.missing_phone ?? 0;
  const total = stats?.total_people ?? 0;
  const syncable = stats?.with_phone_via_users ?? 0;

  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: '#E5E1DC' }} data-testid="radio-phone-import-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
          <Phone size={15} style={{ color: '#FFF' }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Bulk Phone Import</p>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>Populate WhatsApp numbers for every staff member · unblocks Radio delivery</p>
        </div>
      </div>

      {/* Coverage cards */}
      {loading ? (
        <p className="text-xs text-center py-4" style={{ color: '#9CA3AF' }}>Loading coverage…</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <div className="rounded-lg p-3" style={{ backgroundColor: '#F7F5F2' }} data-testid="phones-stat-total">
            <div className="flex items-center gap-1.5 mb-1">
              <Users size={12} style={{ color: '#6B7280' }} />
              <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: '#6B7280' }}>Total staff</p>
            </div>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{total}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{stats?.users_with_phone ?? 0}/{stats?.users_total ?? 0} dashboard users</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: '#ECFDF5' }} data-testid="phones-stat-coverage">
            <div className="flex items-center gap-1.5 mb-1">
              <CheckCircle2 size={12} style={{ color: '#059669' }} />
              <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: '#059669' }}>Reachable</p>
            </div>
            <p className="text-lg font-bold" style={{ color: '#059669' }}>{stats?.with_phone ?? 0} <span className="text-xs font-normal">({pct}%)</span></p>
            <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{stats?.with_phone_people_doc ?? 0} HR · {syncable} via users</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: missing > 0 ? '#FEF2F2' : '#F7F5F2' }} data-testid="phones-stat-missing">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle size={12} style={{ color: missing > 0 ? '#DC2626' : '#6B7280' }} />
              <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: missing > 0 ? '#DC2626' : '#6B7280' }}>Missing</p>
            </div>
            <p className="text-lg font-bold" style={{ color: missing > 0 ? '#DC2626' : '#1B2A4A' }}>{missing}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>no phone anywhere</p>
          </div>
          <div className="rounded-lg p-3" style={{ backgroundColor: syncable > 0 ? '#FEF3C7' : '#F7F5F2' }} data-testid="phones-stat-syncable">
            <div className="flex items-center gap-1.5 mb-1">
              <Link2 size={12} style={{ color: syncable > 0 ? '#D97706' : '#6B7280' }} />
              <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: syncable > 0 ? '#D97706' : '#6B7280' }}>Can mirror</p>
            </div>
            <p className="text-lg font-bold" style={{ color: syncable > 0 ? '#D97706' : '#1B2A4A' }}>{syncable}</p>
            <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>from dashboard users</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <button
          onClick={handleSyncFromUsers}
          disabled={syncing || syncable === 0}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FEF3C7]"
          style={{ borderColor: syncable > 0 ? '#F59E0B' : '#E5E1DC', color: syncable > 0 ? '#B45309' : '#6B7280' }}
          data-testid="sync-phones-from-users-btn"
          title="Mirror phones from dashboard user accounts down to matching HR records"
        >
          {syncing ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
          {syncing ? 'Syncing…' : `Sync from Users${syncable > 0 ? ` (${syncable})` : ''}`}
        </button>
        <button
          onClick={handleSyncToUsers}
          disabled={syncingReverse}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
          data-testid="sync-phones-to-users-btn"
          title="After a bulk CSV import: push HR phones up to matching dashboard user accounts"
        >
          {syncingReverse ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} className="rotate-180" />}
          {syncingReverse ? 'Syncing…' : 'Sync to Users'}
        </button>
        <button
          onClick={downloadMissing}
          disabled={missing === 0}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
          data-testid="download-missing-phones-btn"
        >
          <Download size={13} />
          Download missing-phones CSV ({missing})
        </button>
        <label
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-white transition cursor-pointer ${uploading ? 'opacity-60 cursor-wait' : 'hover:opacity-90'}`}
          style={{ backgroundColor: '#2A7B88' }}
          data-testid="upload-phones-btn-label"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? 'Uploading…' : 'Upload filled CSV'}
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
            data-testid="upload-phones-input"
          />
        </label>
      </div>

      <div className="text-[10.5px] p-2 rounded-md" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
        <strong style={{ color: '#1B2A4A' }}>How this works:</strong> Dashboard login accounts (Users tab) and the HR master (People) are two separate lists. <strong>Sync from Users</strong> mirrors phones from the Users side to the matching HR record. <strong>Download missing CSV</strong> gives you only the staff still unreachable. Fill the <code className="px-1 rounded bg-white">phone</code> column in Excel → upload. Country codes are stripped automatically; last 10 digits are stored.
      </div>

      {/* Sync result */}
      {syncResult && (
        <div className="mt-3 rounded-lg border p-3 text-xs" style={{ borderColor: '#F59E0B' }} data-testid="phones-sync-result">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={14} style={{ color: '#D97706' }} />
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>Sync from Users — complete</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><span className="text-[#6B7280]">Mirrored:</span> <strong style={{ color: '#059669' }}>{syncResult.synced}</strong></div>
            <div><span className="text-[#6B7280]">Already set:</span> <strong>{syncResult.skipped_already_set}</strong></div>
            <div><span className="text-[#6B7280]">Dashboard-only:</span> <strong>{syncResult.no_people_match_count}</strong></div>
          </div>
          {syncResult.no_people_match?.length > 0 && (
            <p className="text-[10.5px] mt-1" style={{ color: '#6B7280' }}>
              {syncResult.no_people_match_count} user{syncResult.no_people_match_count === 1 ? '' : 's'} don't have a matching HR record (leader-only logins) — they can still receive Radio drops via their user phone.
            </p>
          )}
        </div>
      )}

      {/* Reverse sync result */}
      {reverseSyncResult && (
        <div className="mt-3 rounded-lg border p-3 text-xs" style={{ borderColor: '#2A7B88' }} data-testid="phones-reverse-sync-result">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={14} className="rotate-180" style={{ color: '#2A7B88' }} />
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>Sync to Users — complete</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><span className="text-[#6B7280]">Users updated:</span> <strong style={{ color: '#059669' }}>{reverseSyncResult.synced}</strong></div>
            <div><span className="text-[#6B7280]">Already set:</span> <strong>{reverseSyncResult.skipped_already_set}</strong></div>
            <div><span className="text-[#6B7280]">No user match:</span> <strong>{reverseSyncResult.no_user_match}</strong></div>
          </div>
        </div>
      )}

      {/* Import result */}
      {result && (
        <div className="mt-3 rounded-lg border p-3 text-xs" style={{ borderColor: result.updated > 0 ? '#059669' : '#E5E1DC' }} data-testid="phones-import-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={14} style={{ color: '#059669' }} />
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>Import complete</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div><span className="text-[#6B7280]">Updated:</span> <strong style={{ color: '#059669' }}>{result.updated}</strong></div>
            <div><span className="text-[#6B7280]">Not matched:</span> <strong style={{ color: result.not_found_count > 0 ? '#DC2626' : '#1B2A4A' }}>{result.not_found_count}</strong></div>
            <div><span className="text-[#6B7280]">Blank phone rows skipped:</span> <strong>{result.skipped_no_phone}</strong></div>
            <div><span className="text-[#6B7280]">Invalid phone rows:</span> <strong style={{ color: result.skipped_invalid_phone > 0 ? '#DC2626' : '#1B2A4A' }}>{result.skipped_invalid_phone}</strong></div>
          </div>
          {result.not_found?.length > 0 && (
            <details>
              <summary className="cursor-pointer text-[11px]" style={{ color: '#DC2626' }}>Show first {Math.min(result.not_found.length, 50)} unmatched rows</summary>
              <ul className="mt-1 max-h-40 overflow-y-auto text-[10.5px]">
                {result.not_found.map((nf, idx) => (
                  <li key={idx} className="py-0.5">Row {nf.row}: <strong>{nf.name || '—'}</strong> <span className="text-[#6B7280]">({nf.reason})</span></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
